import { useMemo, useState } from "react";

import { SLOT_SYMBOLS, formatMultiplier, formatPaws, hasPremiumPerks, parsePaws } from "@koshkodrop/shared";

import { GameShell } from "../components/GameShell";
import { SlotMachine } from "../games/slots/SlotMachine";
import { ApiError, api } from "../lib/api";
import { haptic } from "../lib/telegram";
import { useSession } from "../store/session";

interface Props {
  onBack: () => void;
}

const QUICK = ["50", "100", "200", "500"];
const INSTANT_KEY = "koshkodrop.slots.instant";

export function SlotsPage({ onBack }: Props) {
  const { balance, setBalance, user } = useSession();
  const [stakeText, setStakeText] = useState("50");
  const [reels, setReels] = useState<[string, string, string] | null>(["paw", "fish", "yarn"]);
  const [spinKey, setSpinKey] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [instant, setInstant] = useState(() => localStorage.getItem(INSTANT_KEY) === "1");
  const [result, setResult] = useState<{ payout: string; multiplier: number; kind: string } | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  const premium = hasPremiumPerks(user?.group ?? "user");
  const instantOn = premium && instant;
  const stake = useMemo(() => safeParse(stakeText), [stakeText]);

  const toggleInstant = () => {
    setInstant((value) => {
      const next = !value;
      localStorage.setItem(INSTANT_KEY, next ? "1" : "0");
      haptic.select();
      return next;
    });
  };

  const spin = async () => {
    if (!stake || spinning) return;
    if (stake > balance) {
      haptic.error();
      setError("Не хватает лапок");
      return;
    }

    setError(null);
    setResult(null);
    try {
      const data = await api.spinSlots(stake.toString());
      const nextResult = {
        payout: data.spin.payout,
        multiplier: data.spin.multiplier,
        kind: data.spin.kind,
      };

      setReels(data.spin.reels);
      setBalance(BigInt(data.balance));
      setSpinKey((key) => key + 1);

      if (instantOn) {
        setResult(nextResult);
        if (BigInt(nextResult.payout) > 0n) haptic.success();
        else haptic.tap("light");
      } else {
        setSpinning(true);
        setResult(nextResult);
        haptic.tap("heavy");
      }
    } catch (caught) {
      haptic.error();
      setError(caught instanceof ApiError ? caught.message : "Не удалось крутить");
    }
  };

  return (
    <GameShell title="Котослоты" subtitle="Три барабана, одна линия — платит только тройка" onBack={onBack}>
      <SlotMachine
        reels={reels}
        spinning={spinning}
        spinKey={spinKey}
        onDone={() => {
          setSpinning(false);
          if (result && BigInt(result.payout) > 0n) haptic.success();
        }}
      />

      <div className="grid grid-cols-3 gap-2">
        {SLOT_SYMBOLS.map((symbol) => (
          <div key={symbol.id} className="rounded-2xl border border-white/5 bg-night-850 px-2 py-2 text-center">
            <div className="text-xl">{symbol.emoji}</div>
            <p className="tabular text-[10px] font-bold text-white/45">
              3× {formatMultiplier(symbol.payout3)}
            </p>
          </div>
        ))}
      </div>

      <StakeRow stakeText={stakeText} setStakeText={setStakeText} />

      <button
        type="button"
        onClick={() => {
          if (!premium) {
            haptic.error();
            setError("Мгновенные крутки — для Premium");
            return;
          }
          toggleInstant();
        }}
        className={`flex w-full items-center justify-center gap-2 rounded-2xl border py-2.5 text-sm font-extrabold transition-colors ${
          instantOn
            ? "border-paws-400/40 bg-paws-500/15 text-paws-300"
            : "border-white/8 bg-night-850 text-white/55"
        }`}
      >
        <span>⚡</span>
        {!premium
          ? "Мгновенный режим · Premium"
          : instantOn
            ? "Мгновенный режим: вкл"
            : "Мгновенный режим: выкл"}
      </button>

      {result && !spinning && (
        <p
          className={`rounded-2xl px-4 py-3 text-center text-sm font-extrabold ${
            BigInt(result.payout) > 0n ? "bg-win-500/15 text-win-400" : "bg-night-850 text-white/45"
          }`}
        >
          {BigInt(result.payout) > 0n
            ? `+${formatPaws(BigInt(result.payout))} 🐾 · ${formatMultiplier(result.multiplier)}`
            : "Пусто — нужна тройка"}
        </p>
      )}

      {error && <p className="text-center text-sm font-semibold text-lose-400">{error}</p>}

      <button
        type="button"
        onClick={() => void spin()}
        disabled={spinning || !stake}
        className="w-full rounded-2xl bg-paws-500 py-4 text-lg font-extrabold text-night-950 active:scale-[0.98] disabled:opacity-35"
      >
        {spinning ? "Крутим…" : instantOn ? "Крутить мгновенно" : "Крутить"}
      </button>
    </GameShell>
  );
}

function StakeRow({
  stakeText,
  setStakeText,
}: {
  stakeText: string;
  setStakeText: (value: string) => void;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 rounded-2xl bg-night-850 px-3 py-2.5">
        <input
          value={stakeText}
          onChange={(event) => setStakeText(event.target.value.replace(/[^\d.,]/g, ""))}
          inputMode="decimal"
          className="tabular w-full bg-transparent text-lg font-bold text-white outline-none"
        />
        <span className="text-paws-400">🐾</span>
      </div>
      <div className="flex gap-2">
        {QUICK.map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => {
              haptic.select();
              setStakeText(value);
            }}
            className={`flex-1 rounded-xl py-2 text-sm font-bold ${
              stakeText === value ? "bg-paws-500 text-night-950" : "bg-night-850 text-white/55"
            }`}
          >
            {value}
          </button>
        ))}
      </div>
    </div>
  );
}

function safeParse(text: string): bigint | null {
  try {
    return parsePaws(text.replace(",", ".").trim() || "0");
  } catch {
    return null;
  }
}
